import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import PhotoGallery from "@/components/PhotoGallery";
import NearbyCitiesLinks from "@/components/NearbyCitiesLinks";
import { useSeo, JsonLd } from "@/lib/seo";
import { Building2, Briefcase, Gift, GlassWater, Trophy, Users2 } from "lucide-react";
import { CatalogPackages } from "@/components/CatalogPackages";

const ENT_PHOTOS = [1, 4, 5, 6, 7, 9, 10, 11].map(
  (n) => `/assets/entreprises/entreprise-${String(n).padStart(2, "0")}.jpg`
);

const CONTEXTES = [
  { icon: Building2, t: "Soirée d'entreprise", d: "Soirées annuelles, fêtes du personnel, soirées de fin d'année dans une ambiance fédératrice." },
  { icon: Users2, t: "Séminaires", d: "Soirée de team building, convention, soirée partenaires ou clôture de séminaire." },
  { icon: Trophy, t: "Galas", d: "Galas, remises de prix, soirées prestige avec sonorisation, lumière et micros pour discours." },
  { icon: GlassWater, t: "Cocktails & inaugurations", d: "Inauguration de site, lancement de produit, cocktail d'accueil élégant." },
  { icon: Gift, t: "Arbres de Noël", d: "Animations pour arbres de Noël, soirées familiales d'entreprise." },
  { icon: Briefcase, t: "Afterworks", d: "Afterworks décontractés, soirées clients, animations relationnelles." },
];

export default function Entreprises() {
  useSeo({
    title: "DJ entreprise Lille & Hauts-de-France | My Animation",
    description:
      "DJ pour soirée d'entreprise, gala, séminaire, inauguration, afterwork et événement professionnel à Lille, dans le Nord et les Hauts-de-France.",
    path: "/entreprises",
    keywords: "DJ entreprise Lille, DJ gala Hauts-de-France, DJ séminaire Nord, soirée corporate, afterwork Lille",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "DJ Entreprise & Événementiel Corporate",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Prestations pour soirées d'entreprise, séminaires, galas, inaugurations, cocktails professionnels, arbres de Noël, afterworks et événements corporate.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Entreprises"
        title={<>Des soirées <span className="italic gold-text">à la hauteur</span><br/>de votre image.</>}
        subtitle="Prestations pour soirées d'entreprise, séminaires, galas, inaugurations, cocktails professionnels, arbres de Noël, afterworks et événements corporate à Lille, Tourcoing, Roubaix, Marcq-en-Barœul, Villeneuve-d'Ascq et dans toute la métropole."
        img="/assets/entreprises/entreprise-04.jpg"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Pourquoi My Animation</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Un partenaire <span className="italic gold-text">fiable.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              My Animation accompagne les entreprises du Nord, du Pas-de-Calais et de la
              Belgique pour leurs événements professionnels. Notre exigence : la discrétion,
              la ponctualité et la qualité du livrable. Un seul interlocuteur, un devis clair,
              une facturation entreprise simple.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Devis & contrat sous 24h",
                "Facturation entreprise",
                "Matériel professionnel",
                "Régie son, lumière, vidéo",
                "Coordination avec vos prestataires",
                "Discrétion et professionnalisme",
                "Sonorisation pour discours",
                "Intervention dans tout le Nord & Pas-de-Calais",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Contextes</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Tous les <span className="italic gold-text">événements professionnels.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CONTEXTES.map(({ icon: Icon, t, d }) => (
              <div key={t} className="card-luxe p-8 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CatalogPackages
        category="entreprise"
        title={<>Nos forfaits <span className="italic gold-text">Corporate.</span></>}
        subtitle={(n) => `${n || "Nos"} formule${n > 1 ? "s" : ""} pour couvrir de l'afterwork au gala VIP. Devis personnalisé sur demande.`}
        testidPrefix="entreprise-pack"
        footerNote="Tarifs TTC · TVA non applicable art. 293 B du CGI · Devis personnalisé selon vos besoins"
      />

      <PhotoGallery
        images={ENT_PHOTOS}
        eyebrow="Nos réalisations"
        title={<>Soirées d'<span className="italic gold-text">entreprise animées.</span></>}
        subtitle="Quelques moments capturés lors de prestations corporate animées par My Animation."
        testIdPrefix="ent-gallery"
      />

      <NearbyCitiesLinks
        eyebrow="DJ entreprise près de chez vous"
        title={<>DJ entreprise <span className="italic gold-text">dans votre ville.</span></>}
        urlPrefix="dj-entreprise"
        intro="My Animation accompagne les entreprises du Nord, du Pas-de-Calais et des Hauts-de-France pour leurs séminaires, galas, arbres de Noël, cocktails et afterworks. Voici nos pages dédiées ville par ville."
        citySlugs={["lille", "tourcoing", "roubaix", "armentieres", "dunkerque", "lens", "arras", "valenciennes", "cambrai", "bethune"]}
        testidPrefix="entreprise-nearby"
      />

      <CTAFooter />
    </>
  );
}
